import React, { useState, useEffect } from 'react';
import { X, History, Download, Clock } from 'lucide-react';
import { dataRoomAPI } from '../../../services/api';

function formatSize(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0, n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

export default function VersionHistoryModal({ open, onClose, dataRoomId, file }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !file) return;
    setLoading(true);
    dataRoomAPI.listFileVersions(dataRoomId, file.file_id || file.id)
      .then(res => setVersions((res.data.data || []).sort((a, b) => b.version - a.version)))
      .catch(() => setVersions([]))
      .finally(() => setLoading(false));
  }, [open, file, dataRoomId]);

  if (!open || !file) return null;

  const handleDownload = async (v) => {
    try {
      const res = await dataRoomAPI.getDownloadUrl(dataRoomId, v.id);
      const a = document.createElement('a');
      a.href = res.data.url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.download = `${file.name}-v${v.version}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      alert('Failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" data-testid="versions-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl shadow-xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2 min-w-0">
            <History size={18} className="text-blue-600 shrink-0" />
            <h2 className="text-lg font-semibold text-gray-900 truncate">Version History — {file.name}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-auto p-5">
          {loading ? (
            <div className="py-8 text-center text-gray-500 text-sm">Loading...</div>
          ) : versions.length === 0 ? (
            <div className="py-8 text-center text-gray-500 text-sm">No versions</div>
          ) : (
            <div className="space-y-2" data-testid="versions-list">
              {versions.map((v, i) => (
                <div key={v.id} className={`border rounded-lg p-3 ${v.is_current ? 'border-blue-300 bg-blue-50/30' : ''}`} data-testid={`version-${v.version}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">v{v.version}</span>
                        {v.is_current && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">CURRENT</span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500 mt-1">
                        <Clock size={10} className="inline mr-1" />
                        {new Date(v.uploaded_at).toLocaleString()} • {formatSize(v.size)} • by {v.uploader_name || 'unknown'}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(v)}
                      className="inline-flex items-center px-2.5 py-1 border rounded-lg text-xs hover:bg-gray-50"
                      data-testid={`download-v${v.version}`}
                    >
                      <Download size={11} className="mr-1" /> Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
